// 거래명세서 항목별 「디자인 완료/전」 배지 (2026-09-06 대표 지시)
// 근거 = 의뢰서(workOrders) · 수집 케이스(parsedCases)의 processStage·designDone·status.
// 4열 규칙 utils/shared/clinic-stage.js의 stageColOf(구 utils/clinicStage.js와 동일 로직)를 그대로 써서 제작 현황 보드와 어긋나지 않게 한다 — 문구만 명세서용.
//   밀링 이상(milling / shipping / done / status completed) → '밀링·완료'   (state 'done')
//   designDone === true                                     → '디자인 완료'  (state 'done')
//   designing(!done) / scanning / received                  → '디자인 전'   (state 'pending')
//   근거 없음(수기 항목·연결 안 됨·구데이터)                   → '확인 불가'  (state 'unknown', 회색)
// 저장은 state 3값('done'|'pending'|'unknown')만 items[].designState에 기록한다 — 매칭 키·단가 로직 무관.
import { stageColOf, isClosedStage } from './shared/clinic-stage';   // 1.3.0(2026-10-12): 공통 정본 사본 직접 사용 — 결과 동일

// statementItems.js가 이 파일을 import하므로 getTimestamp를 되가져오지 않는다(순환 방지) — 같은 규칙의 최소 사본
const getTimestamp = (v) => {
    if (!v) return 0;
    if (v.seconds !== undefined) return v.seconds * 1000;
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    return new Date(v).getTime();
};

const STAGE_KO = { received: '접수', scanning: '스캔', designing: '디자인 중', milling: '밀링', shipping: '완료', done: '완료' };

const fmtAt = (v) => {
    const t = getTimestamp(v);
    if (!t || isNaN(t)) return '';
    const d = new Date(t);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** 이미 로드한 의뢰서/케이스 객체에서 판정에 필요한 필드만 뽑는다 (case: 가상 주문은 _case 폴백). 근거 필드가 하나도 없으면 null */
export const designSrcOf = (o) => {
    if (!o) return null;
    const pick = (x, from) => {
        if (!x) return null;
        if (x.processStage === undefined && x.designDone === undefined && x.status !== 'completed') return null;
        return {
            from,
            processStage: x.processStage ?? null,
            designDone: x.designDone ?? null,
            status: x.status ?? null,
            stageUpdatedAt: x.stageUpdatedAt ?? null,
            designDoneAt: x.designDoneAt ?? null,
        };
    };
    const isCase = String(o.id || '').startsWith('case:');
    return (isCase ? null : pick(o, 'workOrders')) || pick(o._case, 'parsedCases');
};

/**
 * @param {object|null} src designSrcOf 결과
 * @returns {{ state:'done'|'pending'|'unknown', label:string, detail:string }}
 */
export const designStateOf = (src) => {
    if (!src) return { state: 'unknown', label: '확인 불가', detail: '공정 기록이 없는 항목입니다 (수기 입력 또는 의뢰서 연결 없음)' };
    const stage = src.processStage;
    // 4열 키(confirm/design/mill, 종결=null — 2026-09-29). 종결 여부는 isClosedStage 한 곳.
    const closed = isClosedStage(src);
    const col = stageColOf(stage, src.designDone);
    const stageTxt = src.status === 'completed' ? '완료(status completed)' : (STAGE_KO[stage] || stage || '없음');
    const parts = [`공정: ${stageTxt}`, `디자인 완료 표시: ${src.designDone === true ? '예' : src.designDone === false ? '아니오' : '없음'}`];
    const upd = fmtAt(src.stageUpdatedAt);
    if (upd) parts.push(`공정 갱신: ${upd}`);
    // 디자인 완료 시각은 designDoneAt이 있을 때만 — 없으면 '기록 없음'으로 명시해 공정 갱신 시각이 완료 시각처럼 보이지 않게 한다
    // (에이전트 markDesignDone이 시각을 안 남기는 케이스 — 2026-09-29 검토 P1-1)
    const dd = fmtAt(src.designDoneAt);
    if (dd) parts.push(`디자인 완료 시각: ${dd}`);
    else if (col === 'design') parts.push('디자인 완료 시각: 기록 없음');
    parts.push(`근거: ${src.from === 'parsedCases' ? '수집 케이스' : '의뢰서'}`);
    const detail = parts.join(' · ');

    if (closed || col === 'mill') return { state: 'done', label: '밀링·완료', detail };
    if (src.designDone === true || col === 'design') return { state: 'done', label: '디자인 완료', detail };
    if (col === 'confirm') return { state: 'pending', label: '디자인 전', detail };
    return { state: 'unknown', label: '확인 불가', detail };
};

/** 저장된 designState(3값)만 있을 때의 표시 — 열람·인쇄 화면용. 구데이터(값 없음)는 null(배지 생략) */
export const designBadgeFromSaved = (state) => {
    if (state === 'done') return { state, label: '디자인 완료', detail: '발행 시점의 공정 기록 기준' };
    if (state === 'pending') return { state, label: '디자인 전', detail: '발행 시점의 공정 기록 기준' };
    if (state === 'unknown') return { state, label: '확인 불가', detail: '발행 시점에 공정 기록이 없던 항목' };
    return null;
};

const TONE = {
    done: { background: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' },
    pending: { background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' },
    unknown: { background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' },
};

/**
 * 배지 — 화면: 색 칩(no-print) / 인쇄: 소형 글자 "디자인 완료/전" (print-only). '확인 불가'는 인쇄물에 찍지 않는다.
 * @param {{ badge: {state,label,detail}|null, style?:object }} props
 */
export function DesignBadge({ badge, style }) {
    if (!badge) return null;
    const tone = TONE[badge.state] || TONE.unknown;
    return (
        <>
            <span className="no-print" title={badge.detail}
                style={{ display: 'inline-block', fontSize: 10, fontWeight: 800, padding: '1px 7px', borderRadius: 5, whiteSpace: 'nowrap', verticalAlign: 'middle', ...tone, ...(style || {}) }}>
                {badge.label}
            </span>
            {badge.state !== 'unknown' && (
                <span className="print-only" style={{ display: 'none', fontSize: 9, color: '#475569', marginLeft: 4, whiteSpace: 'nowrap' }}>
                    {`(${badge.state === 'done' ? '디자인 완료' : '디자인 전'})`}
                </span>
            )}
        </>
    );
}
